import { useCallback, useEffect, useState } from "react";
import { useAuth } from "context/AuthContext/AuthContextProvider";
import { checkHlApiKey, setupHlAgent } from "./api/hyperliquid";
import useWallet from "./wallets/useWallet";

export type HlSetupStatus = "idle" | "checking" | "setting-up" | "ready" | "error";

export function useHyperliquidSetup() {
  const { account } = useWallet();
  const { token, isAuthenticated } = useAuth();
  const [status, setStatus] = useState<HlSetupStatus>("idle");
  const [error, setError] = useState<string | null>(null);
  const [agentAddress, setAgentAddress] = useState<string | null>(null);

  const check = useCallback(async () => {
    if (!account) return;
    setStatus("checking");
    setError(null);
    try {
      const hasKey = await checkHlApiKey(account);
      setStatus(hasKey ? "ready" : "idle");
    } catch (err: any) {
      setStatus("error");
      setError(err.message ?? "Failed to check Hyperliquid setup");
    }
  }, [account]);

  const setup = useCallback(async () => {
    if (!account || !token) {
      setError("Please connect wallet and authenticate first");
      return;
    }
    setStatus("setting-up");
    setError(null);
    try {
      const result = await setupHlAgent(token);
      setAgentAddress(result.agentWalletAddress);
      setStatus("ready");
    } catch (err: any) {
      setStatus("error");
      setError(err.message ?? "Hyperliquid setup failed");
    }
  }, [account, token]);

  // Auto-check when wallet connects and user is authenticated
  useEffect(() => {
    if (account && isAuthenticated) {
      check();
    } else {
      setStatus("idle");
      setAgentAddress(null);
    }
  }, [account, isAuthenticated, check]);

  return {
    status,
    isReady: status === "ready",
    isLoading: status === "checking" || status === "setting-up",
    error,
    agentAddress,
    setup,
    refetch: check,
  };
}
